import React, { createContext, useContext, useCallback, useMemo, useEffect } from 'react';
import type { LayoutPanel, LayoutSection, LayoutComponent } from '../types/layout';
import type { ComponentSlug } from '../pages/registry/componentRegistry';
import { slugToLabel } from '../pages/registry/componentRegistry';

const BOTTOM_PANEL_SECTION_SLUG = 'active-data-layers';

export interface EditableLayout {
  leftPanel: LayoutPanel | null;
  rightPanel: LayoutPanel | null;
  /** Bottom panel has one section (active-data-layers); we derive bottomPanelInitialLayers from it */
  bottomPanel: LayoutPanel | null;
}

interface EditLayoutContextValue {
  editableLayout: EditableLayout;
  setEditableLayout: React.Dispatch<React.SetStateAction<EditableLayout>>;
  toggleLeftPanelComponent: (slug: ComponentSlug) => void;
  toggleRightPanelComponent: (slug: ComponentSlug) => void;
  addComponentToSection: (panelType: 'left' | 'right', sectionSlug: string, componentSlug: ComponentSlug) => void;
  removeComponentFromSection: (panelType: 'left' | 'right', sectionSlug: string, componentSlug: ComponentSlug) => void;
  addSectionToPanel: (panelType: 'left' | 'right', sectionSlug: string, sectionTitle: string) => void;
  removeSectionFromPanel: (panelType: 'left' | 'right', sectionSlug: string) => void;
  moveSection: (panelType: 'left' | 'right', sectionSlug: string, direction: 'up' | 'down') => void;
  moveComponentInSection: (panelType: 'left' | 'right', sectionSlug: string, componentSlug: ComponentSlug, direction: 'up' | 'down') => void;
  moveComponentToSection: (panelType: 'left' | 'right', componentSlug: ComponentSlug, toSectionSlug: string) => void;
  addBottomPanelLayer: (componentSlug: ComponentSlug) => void;
  removeBottomPanelLayer: (componentSlug: ComponentSlug) => void;
  moveBottomPanelLayer: (componentSlug: ComponentSlug, direction: 'up' | 'down') => void;
  updateComponentDefaultValues: (panelType: 'left' | 'right' | 'bottom', sectionSlug: string, componentSlug: ComponentSlug, values: Record<string, unknown> | null) => void;
}

/** Converts bottom panel to the format expected by MapController */
export function bottomPanelToInitialLayers(panel: LayoutPanel | null): Record<string, Record<string, unknown> | null | boolean> | null {
  if (!panel?.sections?.length) return null;
  const section = panel.sections.find((s) => s.section_slug === BOTTOM_PANEL_SECTION_SLUG);
  if (!section?.components?.length) return null;
  const out: Record<string, boolean> = {};
  for (const c of section.components) {
    out[c.component_slug] = true;
  }
  return Object.keys(out).length > 0 ? out : null;
}

const EditLayoutContext = createContext<EditLayoutContextValue | null>(null);

type ComponentAssignment = { slug: string; component_default_values: Record<string, unknown> | null };

function buildSection(
  sectionSlug: string,
  sectionTitle: string,
  sectionOrder: number,
  components: ComponentAssignment[]
): LayoutSection {
  const layoutComponents: LayoutComponent[] = components.map((c, i) => ({
    component_slug: c.slug,
    component_name: slugToLabel(c.slug),
    component_kind: 'card',
    component_order: i,
    component_default_values: c.component_default_values,
  }));
  return {
    section_slug: sectionSlug,
    section_title: sectionTitle,
    section_collapsible: false,
    section_default_open: false,
    section_order: sectionOrder,
    components: layoutComponents,
  };
}

function buildPanelFromSections(
  panelSlug: string,
  sections: Array<{ section_slug: string; section_title: string; components: ComponentAssignment[] }>
): LayoutPanel {
  const layoutSections: LayoutSection[] = sections.map((s, i) =>
    buildSection(s.section_slug, s.section_title, i, s.components)
  );
  return { panel_slug: panelSlug, sections: layoutSections };
}

function getSectionAssignments(panel: LayoutPanel | null): Record<string, ComponentAssignment[]> {
  const result: Record<string, ComponentAssignment[]> = {};
  if (!panel?.sections?.length) return result;
  for (const section of panel.sections) {
    const comps = section.components
      .sort((a, b) => a.component_order - b.component_order)
      .map((c) => ({ slug: c.component_slug, component_default_values: c.component_default_values }));
    result[section.section_slug] = comps;
  }
  return result;
}

function slugsFromPanel(panel: LayoutPanel | null): string[] {
  if (!panel?.sections?.length) return [];
  return panel.sections.flatMap((s) => s.components.map((c) => c.component_slug));
}

function buildBottomPanel(slugs: string[]): LayoutPanel {
  const components: ComponentAssignment[] = slugs.map((slug) => ({ slug, component_default_values: null }));
  const section = buildSection(BOTTOM_PANEL_SECTION_SLUG, 'Active Data Layers', 0, components);
  return { panel_slug: 'bottom-panel', sections: [section] };
}

function getBottomPanelSlugs(panel: LayoutPanel | null): string[] {
  if (!panel?.sections?.length) return [];
  const section = panel.sections.find((s) => s.section_slug === BOTTOM_PANEL_SECTION_SLUG);
  if (!section) return [];
  return section.components
    .sort((a, b) => a.component_order - b.component_order)
    .map((c) => c.component_slug);
}

function panelFromApiPanel(apiPanel: LayoutPanel | null, panelSlug: string): LayoutPanel {
  if (apiPanel?.sections?.length) {
    const sections = apiPanel.sections
      .sort((a, b) => a.section_order - b.section_order)
      .map((s) => ({
        section_slug: s.section_slug,
        section_title: s.section_title || s.section_slug,
        components: (s.components || [])
          .sort((a, b) => a.component_order - b.component_order)
          .map((c) => ({ slug: c.component_slug, component_default_values: c.component_default_values ?? null })),
      }));
    return buildPanelFromSections(panelSlug, sections);
  }
  return { panel_slug: panelSlug, sections: [] };
}

const EMPTY_LAYOUT: EditableLayout = {
  leftPanel: { panel_slug: 'left-panel', sections: [] },
  rightPanel: { panel_slug: 'right-panel', sections: [] },
  bottomPanel: buildBottomPanel([]),
};

function normalizeBottomLayers(
  layers: Record<string, Record<string, unknown> | null | boolean> | null | undefined
): Record<string, Record<string, unknown> | null> | null {
  if (!layers || typeof layers !== 'object') return null;
  const out: Record<string, Record<string, unknown> | null> = {};
  for (const [k, v] of Object.entries(layers)) {
    if (v === true) out[k] = { visible: true };
    else if (v && typeof v === 'object') out[k] = v as Record<string, unknown>;
    else if (v !== false && v !== null) out[k] = { visible: true };
  }
  return Object.keys(out).length > 0 ? out : null;
}

export function EditLayoutProvider({
  children,
  isEditMode,
  apiLeftPanel,
  apiRightPanel,
  apiBottomPanel,
}: {
  children: React.ReactNode;
  isEditMode: boolean;
  apiLeftPanel: LayoutPanel | null;
  apiRightPanel: LayoutPanel | null;
  /** Bottom panel from API (same as left/right). When null or empty, seed with no layers. */
  apiBottomPanel: LayoutPanel | null;
}) {
  const [editableLayout, setEditableLayout] = React.useState<EditableLayout>(EMPTY_LAYOUT);
  const lastSeedKeyRef = React.useRef<string | null>(null);

  // Seed from API layout when entering edit mode or when page changes. Same pattern as left/right: use API panel when present.
  useEffect(() => {
    if (!isEditMode) return;
    const bottomPanel =
      (apiBottomPanel?.sections?.length ?? 0) > 0
        ? panelFromApiPanel(apiBottomPanel, 'bottom-panel')
        : buildBottomPanel([]);

    const seedKey = [
      JSON.stringify(apiLeftPanel?.sections?.map((s) => s.section_slug) ?? []),
      JSON.stringify(apiRightPanel?.sections?.map((s) => s.section_slug) ?? []),
      slugsFromPanel(apiLeftPanel).join(','),
      slugsFromPanel(apiRightPanel).join(','),
      JSON.stringify(apiBottomPanel),
    ].join('|');
    if (lastSeedKeyRef.current === seedKey) return;
    lastSeedKeyRef.current = seedKey;

    setEditableLayout({
      leftPanel: panelFromApiPanel(apiLeftPanel, 'left-panel'),
      rightPanel: panelFromApiPanel(apiRightPanel, 'right-panel'),
      bottomPanel,
    });
  }, [isEditMode, apiLeftPanel, apiRightPanel, apiBottomPanel]);

  const toggleLeftPanelComponent = useCallback((slug: ComponentSlug) => {
    setEditableLayout((prev) => {
      const assignments = getSectionAssignments(prev.leftPanel);
      const current = slugsFromPanel(prev.leftPanel);
      const sectionOrder = [...prev.leftPanel!.sections].sort((a, b) => a.section_order - b.section_order).map((s) => s.section_slug);
      const firstSection = sectionOrder[0];
      if (current.includes(slug)) {
        for (const key of Object.keys(assignments)) {
          assignments[key] = assignments[key].filter((c) => c.slug !== slug);
        }
      } else if (firstSection) {
        const comps = [...(assignments[firstSection] ?? []), { slug, component_default_values: null }];
        comps.sort((a, b) => a.slug.localeCompare(b.slug));
        assignments[firstSection] = comps;
      }
      const sections = sectionOrder.map((secSlug) => ({
        section_slug: secSlug,
        section_title: prev.leftPanel!.sections.find((s) => s.section_slug === secSlug)?.section_title ?? secSlug,
        components: assignments[secSlug] ?? [],
      }));
      return { ...prev, leftPanel: buildPanelFromSections('left-panel', sections) };
    });
  }, []);

  const toggleRightPanelComponent = useCallback((slug: ComponentSlug) => {
    setEditableLayout((prev) => {
      const assignments = getSectionAssignments(prev.rightPanel);
      const current = slugsFromPanel(prev.rightPanel);
      const sectionOrder = [...prev.rightPanel!.sections].sort((a, b) => a.section_order - b.section_order).map((s) => s.section_slug);
      const firstSection = sectionOrder[0];
      if (current.includes(slug)) {
        for (const key of Object.keys(assignments)) {
          assignments[key] = assignments[key].filter((c) => c.slug !== slug);
        }
      } else if (firstSection) {
        const comps = [...(assignments[firstSection] ?? []), { slug, component_default_values: null }];
        comps.sort((a, b) => a.slug.localeCompare(b.slug));
        assignments[firstSection] = comps;
      }
      const sections = sectionOrder.map((secSlug) => ({
        section_slug: secSlug,
        section_title: prev.rightPanel!.sections.find((s) => s.section_slug === secSlug)?.section_title ?? secSlug,
        components: assignments[secSlug] ?? [],
      }));
      return { ...prev, rightPanel: buildPanelFromSections('right-panel', sections) };
    });
  }, []);

  const rebuildPanelFromAssignments = useCallback(
    (panel: LayoutPanel, assignments: Record<string, ComponentAssignment[]>): LayoutPanel => {
      const sectionOrder = [...panel.sections].sort((a, b) => a.section_order - b.section_order).map((s) => s.section_slug);
      const sections = sectionOrder.map((secSlug) => ({
        section_slug: secSlug,
        section_title: panel.sections.find((s) => s.section_slug === secSlug)?.section_title ?? secSlug,
        components: assignments[secSlug] ?? [],
      }));
      return buildPanelFromSections(panel.panel_slug, sections);
    },
    []
  );

  const addComponentToSection = useCallback((panelType: 'left' | 'right', sectionSlug: string, componentSlug: ComponentSlug) => {
    setEditableLayout((prev) => {
      const panel = panelType === 'left' ? prev.leftPanel! : prev.rightPanel!;
      const assignments = getSectionAssignments(panel);
      if (slugsFromPanel(panel).includes(componentSlug)) return prev;
      const comps = [...(assignments[sectionSlug] ?? []), { slug: componentSlug, component_default_values: null }];
      assignments[sectionSlug] = comps;
      const newPanel = rebuildPanelFromAssignments(panel, assignments);
      return panelType === 'left' ? { ...prev, leftPanel: newPanel } : { ...prev, rightPanel: newPanel };
    });
  }, [rebuildPanelFromAssignments]);

  const removeComponentFromSection = useCallback((panelType: 'left' | 'right', sectionSlug: string, componentSlug: ComponentSlug) => {
    setEditableLayout((prev) => {
      const panel = panelType === 'left' ? prev.leftPanel! : prev.rightPanel!;
      const assignments = getSectionAssignments(panel);
      assignments[sectionSlug] = (assignments[sectionSlug] ?? []).filter((c) => c.slug !== componentSlug);
      const newPanel = rebuildPanelFromAssignments(panel, assignments);
      return panelType === 'left' ? { ...prev, leftPanel: newPanel } : { ...prev, rightPanel: newPanel };
    });
  }, [rebuildPanelFromAssignments]);

  const addSectionToPanel = useCallback((panelType: 'left' | 'right', sectionSlug: string, sectionTitle: string) => {
    setEditableLayout((prev) => {
      const panel = panelType === 'left' ? prev.leftPanel! : prev.rightPanel!;
      if (panel.sections.some((s) => s.section_slug === sectionSlug)) return prev;
      const assignments = getSectionAssignments(panel);
      const newSections = [
        ...[...panel.sections].sort((a, b) => a.section_order - b.section_order).map((s) => ({
          section_slug: s.section_slug,
          section_title: s.section_title || s.section_slug,
          components: assignments[s.section_slug] ?? [],
        })),
        { section_slug: sectionSlug, section_title: sectionTitle, components: [] as ComponentAssignment[] },
      ];
      const newPanel = buildPanelFromSections(panel.panel_slug, newSections);
      return panelType === 'left' ? { ...prev, leftPanel: newPanel } : { ...prev, rightPanel: newPanel };
    });
  }, []);

  const removeSectionFromPanel = useCallback((panelType: 'left' | 'right', sectionSlug: string) => {
    setEditableLayout((prev) => {
      const panel = panelType === 'left' ? prev.leftPanel! : prev.rightPanel!;
      const newSections = [...panel.sections]
        .sort((a, b) => a.section_order - b.section_order)
        .filter((s) => s.section_slug !== sectionSlug)
        .map((s) => ({
          section_slug: s.section_slug,
          section_title: s.section_title || s.section_slug,
          components: (s.components || []).sort((a, b) => a.component_order - b.component_order).map((c) => ({ slug: c.component_slug, component_default_values: c.component_default_values })),
        }));
      const newPanel = buildPanelFromSections(panel.panel_slug, newSections);
      return panelType === 'left' ? { ...prev, leftPanel: newPanel } : { ...prev, rightPanel: newPanel };
    });
  }, []);

  const moveSection = useCallback((panelType: 'left' | 'right', sectionSlug: string, direction: 'up' | 'down') => {
    setEditableLayout((prev) => {
      const panel = panelType === 'left' ? prev.leftPanel! : prev.rightPanel!;
      const assignments = getSectionAssignments(panel);
      const order = [...panel.sections].sort((a, b) => a.section_order - b.section_order).map((s) => s.section_slug);
      const idx = order.indexOf(sectionSlug);
      if (idx < 0) return prev;
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= order.length) return prev;
      [order[idx], order[swapIdx]] = [order[swapIdx], order[idx]];
      const newSections = order.map((secSlug) => ({
        section_slug: secSlug,
        section_title: panel.sections.find((s) => s.section_slug === secSlug)?.section_title ?? secSlug,
        components: assignments[secSlug] ?? [],
      }));
      const newPanel = buildPanelFromSections(panel.panel_slug, newSections);
      return panelType === 'left' ? { ...prev, leftPanel: newPanel } : { ...prev, rightPanel: newPanel };
    });
  }, []);

  const moveComponentInSection = useCallback((panelType: 'left' | 'right', sectionSlug: string, componentSlug: ComponentSlug, direction: 'up' | 'down') => {
    setEditableLayout((prev) => {
      const panel = panelType === 'left' ? prev.leftPanel! : prev.rightPanel!;
      const assignments = getSectionAssignments(panel);
      const comps = assignments[sectionSlug] ?? [];
      const idx = comps.findIndex((c) => c.slug === componentSlug);
      if (idx < 0) return prev;
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= comps.length) return prev;
      const next = [...comps];
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      assignments[sectionSlug] = next;
      const newPanel = rebuildPanelFromAssignments(panel, assignments);
      return panelType === 'left' ? { ...prev, leftPanel: newPanel } : { ...prev, rightPanel: newPanel };
    });
  }, [rebuildPanelFromAssignments]);

  const moveComponentToSection = useCallback((panelType: 'left' | 'right', componentSlug: ComponentSlug, toSectionSlug: string) => {
    setEditableLayout((prev) => {
      const panel = panelType === 'left' ? prev.leftPanel! : prev.rightPanel!;
      const assignments = getSectionAssignments(panel);
      let movedComp: ComponentAssignment | null = null;
      for (const key of Object.keys(assignments)) {
        const idx = (assignments[key] ?? []).findIndex((c) => c.slug === componentSlug);
        if (idx >= 0) {
          movedComp = assignments[key][idx];
          assignments[key] = assignments[key].filter((c) => c.slug !== componentSlug);
          break;
        }
      }
      if (movedComp) {
        const comps = [...(assignments[toSectionSlug] ?? []), movedComp];
        comps.sort((a, b) => a.slug.localeCompare(b.slug));
        assignments[toSectionSlug] = comps;
      }
      const newPanel = rebuildPanelFromAssignments(panel, assignments);
      return panelType === 'left' ? { ...prev, leftPanel: newPanel } : { ...prev, rightPanel: newPanel };
    });
  }, [rebuildPanelFromAssignments]);

  const addBottomPanelLayer = useCallback((componentSlug: ComponentSlug) => {
    setEditableLayout((prev) => {
      const assignments = getSectionAssignments(prev.bottomPanel);
      const comps = assignments[BOTTOM_PANEL_SECTION_SLUG] ?? [];
      if (comps.some((c) => c.slug === componentSlug)) return prev;
      const next = [...comps, { slug: componentSlug, component_default_values: null }].sort((a, b) => a.slug.localeCompare(b.slug));
      const newPanel = buildPanelFromSections('bottom-panel', [
        { section_slug: BOTTOM_PANEL_SECTION_SLUG, section_title: 'Active Data Layers', components: next },
      ]);
      return { ...prev, bottomPanel: newPanel };
    });
  }, []);

  const removeBottomPanelLayer = useCallback((componentSlug: ComponentSlug) => {
    setEditableLayout((prev) => {
      const assignments = getSectionAssignments(prev.bottomPanel);
      const comps = (assignments[BOTTOM_PANEL_SECTION_SLUG] ?? []).filter((c) => c.slug !== componentSlug);
      const newPanel = buildPanelFromSections('bottom-panel', [
        { section_slug: BOTTOM_PANEL_SECTION_SLUG, section_title: 'Active Data Layers', components: comps },
      ]);
      return { ...prev, bottomPanel: newPanel };
    });
  }, []);

  const moveBottomPanelLayer = useCallback((componentSlug: ComponentSlug, direction: 'up' | 'down') => {
    setEditableLayout((prev) => {
      const assignments = getSectionAssignments(prev.bottomPanel);
      const comps = assignments[BOTTOM_PANEL_SECTION_SLUG] ?? [];
      const idx = comps.findIndex((c) => c.slug === componentSlug);
      if (idx < 0) return prev;
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= comps.length) return prev;
      const next = [...comps];
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      const newPanel = buildPanelFromSections('bottom-panel', [
        { section_slug: BOTTOM_PANEL_SECTION_SLUG, section_title: 'Active Data Layers', components: next },
      ]);
      return { ...prev, bottomPanel: newPanel };
    });
  }, []);

  const updateComponentDefaultValues = useCallback(
    (panelType: 'left' | 'right' | 'bottom', sectionSlug: string, componentSlug: ComponentSlug, values: Record<string, unknown> | null) => {
      setEditableLayout((prev) => {
        if (panelType === 'bottom') {
          const assignments = getSectionAssignments(prev.bottomPanel);
          const comps = assignments[BOTTOM_PANEL_SECTION_SLUG] ?? [];
          const idx = comps.findIndex((c) => c.slug === componentSlug);
          if (idx < 0) return prev;
          const next = [...comps];
          next[idx] = { ...next[idx], component_default_values: values };
          const newPanel = buildPanelFromSections('bottom-panel', [
            { section_slug: BOTTOM_PANEL_SECTION_SLUG, section_title: 'Active Data Layers', components: next },
          ]);
          return { ...prev, bottomPanel: newPanel };
        }
        const panel = panelType === 'left' ? prev.leftPanel! : prev.rightPanel!;
        const assignments = getSectionAssignments(panel);
        const comps = assignments[sectionSlug] ?? [];
        const idx = comps.findIndex((c) => c.slug === componentSlug);
        if (idx < 0) return prev;
        const next = [...comps];
        next[idx] = { ...next[idx], component_default_values: values };
        assignments[sectionSlug] = next;
        const newPanel = rebuildPanelFromAssignments(panel, assignments);
        return panelType === 'left' ? { ...prev, leftPanel: newPanel } : { ...prev, rightPanel: newPanel };
      });
    },
    [rebuildPanelFromAssignments]
  );

  const value = useMemo<EditLayoutContextValue>(
    () => ({
      editableLayout,
      setEditableLayout,
      toggleLeftPanelComponent,
      toggleRightPanelComponent,
      addComponentToSection,
      removeComponentFromSection,
      addSectionToPanel,
      removeSectionFromPanel,
      moveSection,
      moveComponentInSection,
      moveComponentToSection,
      addBottomPanelLayer,
      removeBottomPanelLayer,
      moveBottomPanelLayer,
      updateComponentDefaultValues,
    }),
    [
      editableLayout,
      toggleLeftPanelComponent,
      toggleRightPanelComponent,
      addComponentToSection,
      removeComponentFromSection,
      addSectionToPanel,
      removeSectionFromPanel,
      moveSection,
      moveComponentInSection,
      moveComponentToSection,
      addBottomPanelLayer,
      removeBottomPanelLayer,
      moveBottomPanelLayer,
      updateComponentDefaultValues,
    ]
  );

  return (
    <EditLayoutContext.Provider value={value}>
      {children}
    </EditLayoutContext.Provider>
  );
}

export function useEditLayout(): EditLayoutContextValue | null {
  return useContext(EditLayoutContext);
}
